/**
 * Per-day route popup fed by computeDay output. Every step of the day appears:
 * base → curated transfer waypoints (piers/airports/stations, FixedItem.g) →
 * lodging arrivals (at:) → ✔/🔒 activity stops → base. Legs between stops are
 * real street routes from OSRM; sea/air crossings (FixedItem.sea, or >30 km)
 * draw as dashed great-circle lines. Stops <50 m apart dedupe. A curated
 * waypoint that only anticipates where the day MIGHT go (see buildDayStops)
 * is skipped in favor of the actual pick.
 */
import L from "leaflet";
import { computeDay, type DayComputation, type SlotRowComp } from "@trip/shared/engine/schedule";
import { esc, fmin, t12 } from "@trip/shared/engine/time";
import { hav, type LatLng } from "@trip/shared/engine/travel";
import { isFixed, type DayDef, type Slots, type TripDefinition } from "@trip/shared/types";
import { store } from "../store.js";
import { banner } from "./banner.js";
import { setDayMapDispose } from "./modal.js";

const OSRM = "https://router.project-osrm.org/route/v1/driving";
const VALHALLA = "https://valhalla1.openstreetmap.de/route";
/** leg geometry cache: "mode|lat,lng|lat,lng" → route coords (or null = use straight) */
const legCache = new Map<string, LatLng[] | null>();

/** Google encoded polyline, precision 6 (what Valhalla returns). */
function decodePolyline6(str: string): LatLng[] {
  const out: LatLng[] = [];
  let lat = 0, lng = 0, i = 0;
  while (i < str.length) {
    for (const which of [0, 1]) {
      let shift = 0, result = 0, b: number;
      do {
        b = str.charCodeAt(i++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (which === 0) lat += delta;
      else lng += delta;
    }
    out.push([lat / 1e6, lng / 1e6]);
  }
  return out;
}

/**
 * Short hops route on foot: Dubrovnik's Old Town is a pedestrian zone, and a
 * driving profile snaps both ends to roads OUTSIDE the walls — the "route"
 * became a car loop around the city that never touched the pins. Valhalla
 * (already the directions provider) has a pedestrian costing; OSRM's public
 * server is driving-only, so it keeps only the genuinely drivable legs.
 */
const WALK_LEG_KM = 2.5;

async function streetRoute(a: LatLng, b: LatLng): Promise<LatLng[] | null> {
  const walk = hav(a, b) <= WALK_LEG_KM;
  const key = `${walk ? "w" : "d"}|${a[0].toFixed(5)},${a[1].toFixed(5)}|${b[0].toFixed(5)},${b[1].toFixed(5)}`;
  if (legCache.has(key)) return legCache.get(key)!;
  try {
    let out: LatLng[] | null = null;
    if (walk) {
      const res = await fetch(VALHALLA, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          locations: [{ lat: a[0], lon: a[1] }, { lat: b[0], lon: b[1] }],
          costing: "pedestrian",
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const j = await res.json();
      const shape: string | undefined = j?.trip?.legs?.[0]?.shape;
      out = shape ? decodePolyline6(shape) : null;
    } else {
      const res = await fetch(`${OSRM}/${a[1]},${a[0]};${b[1]},${b[0]}?overview=full&geometries=geojson`);
      if (!res.ok) throw new Error(String(res.status));
      const j = await res.json();
      const coords: [number, number][] | undefined = j?.routes?.[0]?.geometry?.coordinates;
      out = coords && coords.length > 1 ? coords.map(([lng, lat]) => [lat, lng] as LatLng) : null;
    }
    if (out && out.length > 1) legCache.set(key, out); // cache successes only
    return out;
  } catch {
    return null; // offline / rate-limited / unroutable — straight line this time
  }
}

export interface DayMapStop {
  n: string;
  g: LatLng;
  t: string;
  sea?: boolean;
}

/**
 * The ordered stop list a day's route map draws: base → curated transfer
 * waypoints (piers/airports/stations, FixedItem.g) / lodging arrivals (at:)
 * → ✔ confirmed / 🔒 booked activity picks → base.
 *
 * One curated waypoint is deliberately dropped: a fixed item with a `g` pin
 * but no `e` (it's an arrival, not a timetabled journey with a landing spot)
 * and no `at` (not a lodging arrival either), sitting immediately before a
 * flexible slot, only anticipates where that slot MIGHT lead — e.g. "train
 * to Amsterdam Centraal" before a slot the user actually filled with
 * Utrecht. Drawing it as a real stop routes through a place the day never
 * goes. The engine already agrees: it ignores FixedItem.g for travel
 * chaining (only `at` moves the schedule's origin), so this keeps the map
 * consistent with the schedule it's illustrating.
 */
export function buildDayStops(def: TripDefinition, day: DayDef, slots: Slots, comp: DayComputation): DayMapStop[] {
  const home = def.dayHome[day.id];
  const stops: DayMapStop[] = [];
  const push = (n: string, g: LatLng | null | undefined, t: string, sea = false) => {
    if (!g) return;
    if (stops.length && hav(stops[stops.length - 1].g, g) < 0.05) return;
    stops.push({ n, g, t, sea });
  };

  if (home) push("Start — your base", def.hotelPts[home[0]], t12(day.start));
  day.items.forEach((it, i) => {
    if (isFixed(it)) {
      const next = day.items[i + 1];
      const anticipatesFlexible = !it.e && !it.at && !!next && !isFixed(next);
      if (it.g && !anticipatesFlexible) push(it.l.split("·")[0].trim(), it.g, t12(it.t), !!it.sea);
      else if (it.at && def.hotelPts[it.at]) push(it.l.split("·")[0].trim(), def.hotelPts[it.at], t12(it.t));
    } else {
      const st = slots[`${day.id}_${i}`];
      if (!st || st.dur === 0 || st.st < 1) return; /* confirmed or booked only */
      const r = comp.rows[i] as SlotRowComp;
      if (!r.geo) return;
      push(def.act[st.act].name.split("—")[0].trim(), r.geo, fmin(r.startMin));
    }
  });
  if (home && !def.noEnd[day.id]) push("Back — your base", def.hotelPts[home[1]], "");
  return stops;
}

export function openDayMap(dayId: string) {
  const def = store.def;
  const d = def.days.find((x) => x.id === dayId);
  if (!d) return;
  const comp = computeDay(def, d, store.slots, store.dayEnds[dayId], store.fixDurs);
  const stops = buildDayStops(def, d, store.slots, comp);

  if (stops.length < 2) {
    banner("The route map shows ✔ confirmed / 🔒 booked stops only — confirm some picks on this day first.", "warn");
    return;
  }

  const m = document.getElementById("modal")!;
  m.style.maxWidth = "760px";
  m.innerHTML = `
    <div class="m-head"><div class="m-cat">Route map — stops in order</div><h3>${esc(d.title)}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button></div>
    <div id="daymap" style="height:430px; background:var(--sea-lt);"></div>
    <div class="m-body" style="padding:12px 22px 16px">
      <div style="font-size:12.5px; line-height:1.7; color:var(--ink)">${stops
        .map(
          (x, i) =>
            `<span style="white-space:nowrap"><b style="color:var(--terra)">${i + 1}</b> ${esc(x.n)}${
              x.t ? ` <span style="color:var(--mut)">· ${esc(x.t)}</span>` : ""
            }</span>`
        )
        .join(' <span style="color:var(--sea)">→</span> ')}</div>
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");

  const dayMap = L.map("daymap", { scrollWheelZoom: true, touchZoom: true, doubleClickZoom: true });
  L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18, attribution: "&copy; OpenStreetMap contributors" }).addTo(dayMap);
  let disposed = false;
  setDayMapDispose(() => {
    disposed = true;
    dayMap.remove();
  });

  /* A day usually starts and ends at the same base — two markers on one spot
     hide the lower number and the route looks like it starts at 2. Stops
     sharing a location share one marker labelled with every visit. */
  const groups = new Map<string, { g: LatLng; visits: { n: number; name: string; t: string }[] }>();
  stops.forEach((x, i) => {
    const key = `${x.g[0].toFixed(4)},${x.g[1].toFixed(4)}`;
    if (!groups.has(key)) groups.set(key, { g: x.g, visits: [] });
    groups.get(key)!.visits.push({ n: i + 1, name: x.n, t: x.t });
  });
  for (const { g, visits } of groups.values()) {
    const label = visits.map((v) => v.n).join("·");
    L.marker(g, {
      icon: L.divIcon({ className: "", html: `<span class="mroute">${label}</span>`, iconSize: [24, 24], iconAnchor: [12, 12] }),
      zIndexOffset: 600,
    })
      .bindPopup(visits.map((v) => `<b>${v.n}. ${esc(v.name)}</b>${v.t ? `<br>${esc(v.t)}` : ""}`).join("<hr style='margin:4px 0'>"))
      .addTo(dayMap);
  }
  dayMap.fitBounds(L.latLngBounds(stops.map((x) => x.g)).pad(0.28));
  setTimeout(() => !disposed && dayMap.invalidateSize(), 250);

  /* legs: dashed straight for sea/air crossings, street geometry otherwise */
  stops.slice(0, -1).forEach(async (from, i) => {
    const to = stops[i + 1];
    const crossing = to.sea || hav(from.g, to.g) > 30;
    if (crossing) {
      L.polyline([from.g, to.g], { color: "#0f7b8f", weight: 3, dashArray: "7 8", opacity: 0.8 }).addTo(dayMap);
      return;
    }
    const straight = L.polyline([from.g, to.g], { color: "#e05a3a", weight: 3, opacity: 0.45, dashArray: "2 6" }).addTo(dayMap);
    const route = await streetRoute(from.g, to.g);
    if (disposed) return;
    if (route) {
      straight.remove();
      L.polyline(route, { color: "#e05a3a", weight: 3.5, opacity: 0.85 }).addTo(dayMap);
    } else {
      straight.setStyle({ opacity: 0.85, dashArray: undefined });
    }
  });
}
